import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  FiCamera,
  FiDroplet,
  FiMapPin,
  FiMessageCircle,
  FiScissors,
  FiUser,
} from 'react-icons/fi'
import './hairProfile.css'

// ─── CSS ──────────────────────────────────────────────────────────────────────

// ─── Question metadata — icons + short labels ─────────────────────────────────
const QUESTION_META = [
  { icon: FiUser, label: 'Shopping for' },
  { icon: FiMapPin, label: 'Profile' },
  { icon: FiCamera, label: 'Hair photo' },
  { icon: FiScissors, label: 'Hair type & concerns' },
  { icon: FiDroplet, label: 'Current routine' },
]

/**
 * HairProfile
 *
 * Props:
 *   userName    string        — user's full name
 *   userAvatar  string        — user's profile picture URL
 *   answers     Answer[]      — array of answer objects (one per question)
 *   hairType    string?       — detected hair type e.g. "4B"
 *
 * Answer object shape:
 *   {
 *     questionIndex: number,     // 0–4 matching QUESTION_META
 *     text:          string?,    // text answer
 *     photoUrl:      string?,    // photo URL (for question 2)
 *     tags:          string[]?,  // optional parsed tags shown as chips
 *   }
 *
 * Usage in Chatbot.jsx — add to the AI message that precedes ProductSlider:
 *
 *   {
 *     id: ++uid,
 *     from: 'ai',
 *     text: "Here's your personalised hair profile.",
 *     hairProfile: {
 *       userName:   agentData.agentFullName,
 *       userAvatar: agentData.agentProfilePicture,
 *       hairType:   '4B',
 *       answers:    collectedAnswers,     // built up during question flow
 *     },
 *     time: getTime(),
 *   }
 *
 *   Then in your message render loop:
 *   {m.hairProfile && <HairProfile {...m.hairProfile} />}
 */
export default function HairProfile({
  userName = 'Divine Kalu',
  userAvatar = 'https://api.dicebear.com/7.x/notionists/svg?seed=Divine&backgroundColor=b6e3f4',
  hairType,
  profileItems = [],
  hairPhoto = null,
}) {
  const [expanded, setExpanded] = useState(true)
  const language = useSelector((state) => state.language)
  const translations = useSelector((state) => state.translationsData) || {}

  const getTranslationText = (
    keyPath,
    data = translations,
    lang = language,
  ) => {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const translateText = (keyPath, replacements = {}) => {
    let value = getTranslationText(keyPath)
    if (typeof value === 'string') {
      Object.entries(replacements).forEach(([key, replacement]) => {
        value = value.replace(
          new RegExp(`\\{${key}\\}`, 'g'),
          String(replacement),
        )
      })
    }
    return value
  }

  return (
    <>
      <div className='hp-root'>
        {/* ── Banner ── */}
        <div className='hp-banner'>
          <div className='hp-banner-top'>
            <div className='hp-avatar-wrap'>
              <img src={userAvatar} alt={userName} className='hp-avatar' />
              {hairPhoto && (
                <img src={hairPhoto} alt='Hair' className='hp-hair-thumb' />
              )}
            </div>
            <div className='hp-banner-info'>
              <div className='hp-banner-name'>{userName}</div>
              <div className='hp-banner-sub'>
                {translateText('chat.hairProfile.bannerSub', {
                  count: profileItems.length,
                })}
              </div>
            </div>
          </div>

          {/* Hair type pill */}
          {hairType && (
            <div className='hp-hair-type-pill'>
              <span className='hp-hair-type-dot' />
              {translateText('chat.hairProfile.hairTypeDetected', { hairType })}
            </div>
          )}
        </div>

        {/* ── Answers section ── */}
        <div className='hp-toggle-row'>
          <span className='hp-section-label'>
            {translateText('chat.hairProfile.sectionLabel')}
          </span>
          <button
            className='hp-toggle-btn'
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded
              ? `▲ ${translateText('chat.hairProfile.collapse')}`
              : `▼ ${translateText('chat.hairProfile.expand')}`}
          </button>
        </div>

        {expanded && (
          <div className='hp-answers'>
            {profileItems.map((item, i) => (
              <div
                className='hp-answer-row'
                key={item.label}
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className='hp-icon'>
                  <FiMessageCircle size={14} />
                </div>

                <div className='hp-answer-content'>
                  <div className='hp-answer-question'>{item.label}</div>
                  <div className='hp-answer-text'>{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Footer ── */}
        <div className='hp-footer'>
          <div className='hp-footer-note'>
            <strong>Halisi</strong>{' '}
            {translateText('chat.hairProfile.profileSummary')}
            <br />
            {translateText('chat.hairProfile.personalisedReady')}
          </div>
          <div className='hp-complete-badge'>
            <span className='hp-complete-dot' />
            {translateText('chat.hairProfile.profileComplete')}
          </div>
        </div>
      </div>
    </>
  )
}

// ─── Demo data — replace with real collected answers ──────────────────────────
export const DEMO_ANSWERS = [
  {
    questionIndex: 0,
    text: 'For myself',
    tags: ['Myself'],
  },
  {
    questionIndex: 1,
    text: '26 years old · Female · Lagos, Nigeria',
    tags: ['26 yrs', 'Female', 'Lagos, NG'],
  },
  {
    questionIndex: 2,
    photoUrl:
      'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=200&q=80',
    text: null,
  },
  {
    questionIndex: 3,
    text: 'Main concern: dryness & breakage. Goals: length retention and defined curls. Scalp: slightly dry. Structure: natural 4B/4C.',
    tags: ['Dryness', 'Breakage', 'Natural', '4B / 4C'],
  },
  {
    questionIndex: 4,
    text: 'Currently using a drugstore shampoo and coconut oil. I style once a week. Budget ~€30/month. Open to eco-friendly products. Prefer a simple routine.',
    tags: ['€30 / month', 'Eco-friendly', 'Simple routine', 'Weekly styling'],
  },
]
